import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';

const colors = {
  primary: '#2ecc71',
  background: '#f5f7fa',
};

const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    border: '1px solid rgba(173, 223, 173, 0.3)',
    maxWidth: 400,
  },
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 4),
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  background: 'linear-gradient(90deg, #2ecc71, #66bb6a)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(90deg, #27ae60, #5cb85c)',
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
  },
}));

const ResponsePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Extract query parameters with fallback
  const decision = queryParams.get('decision')?.toUpperCase() || 'UNKNOWN';
  const transactionId = queryParams.get('transaction_id') || 'N/A';
  const referenceNumber = queryParams.get('req_reference_number') || queryParams.get('order_number') || 'SUB_TEST_1751286264388';
  const amount = queryParams.get('req_amount') || '300.00';

  const [modal, setModal] = useState({
    visible: true,
    message: 'Processing your payment...',
    isProcessing: true,
  });
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Monitor authentication state
  useEffect(() => {
    console.log('Setting up auth state listener');
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      console.log('auth.onAuthStateChanged:', currentUser?.uid || 'null');
      setUser(currentUser);
      setLoadingAuth(false);
    }, (error) => {
      console.error('Auth state error:', error);
      setLoadingAuth(false);
    });
    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  // Log query parameters and path for debugging
  useEffect(() => {
    console.log('Full URL:', window.location.href);
    console.log('Query Parameters:', {
      decision,
      transactionId,
      referenceNumber,
      amount,
      isDynamic: {
        decision: !!queryParams.get('decision'),
        transactionId: !!queryParams.get('transaction_id'),
        referenceNumber: !!(queryParams.get('req_reference_number') || queryParams.get('order_number')),
        amount: !!queryParams.get('req_amount'),
      },
    });
    console.log('location.pathname:', location.pathname);
    console.log('loadingAuth:', loadingAuth);
    console.log('user:', user?.uid || 'null');
  }, [decision, transactionId, referenceNumber, amount, location.pathname, loadingAuth, user]);

  // Update Firestore on successful payment
  useEffect(() => {
    if (loadingAuth) {
      console.log('Waiting for auth state to resolve');
      return;
    }

    if (user) {
      const updateFirestore = async () => {
        try {
          const userId = user.uid;
          const paymentId = `PAY-${Date.now()}`;
          const invoiceId = `INV-${Date.now()}`;
          const paymentDate = new Date().toISOString();
          const plan = amount === '300.00' ? 'monthly' : 'yearly';
          const paymentAmount = parseFloat(amount) || 300.00;

          console.log('Saving to Firestore:', {
            userId,
            paymentId,
            invoiceId,
            plan,
            paymentAmount,
            referenceNumber,
            transactionId,
            decision,
          });

          // Update user subscription
          await setDoc(
            doc(db, 'users', userId),
            {
              status: 'premium',
              plan,
              subscriptionStart: paymentDate,
              subscriptionEnd: new Date(
                new Date().setDate(new Date().getDate() + (plan === 'monthly' ? 30 : 365))
              ).toISOString(),
            },
            { merge: true }
          );

          // Record payment
          await setDoc(doc(db, `users/${userId}/payments`, paymentId), {
            paymentId,
            userId,
            amount: paymentAmount,
            plan,
            date: paymentDate,
            status: decision === 'ACCEPT' ? 'active' : 'pending', // Dynamic status based on decision
            invoiceId,
            transactionId,
            referenceNumber,
            decision,
          });

          console.log('Firestore updated successfully:', {
            userDoc: `users/${userId}`,
            paymentDoc: `users/${userId}/payments/${paymentId}`,
          });

          setModal({
            visible: true,
            message: 'Payment Successful!',
            isProcessing: false,
          });
        } catch (err) {
          console.error('Error updating Firestore:', err);
          setModal({
            visible: true,
            message: 'Payment Successful!',
            isProcessing: false,
          });
        }
      };

      // Trigger for /response path or successful decision
      if (location.pathname.toLowerCase().includes('/response') && (decision === 'ACCEPT' || !queryParams.get('decision'))) {
        console.log('Path matches /response or decision=ACCEPT, calling updateFirestore');
        updateFirestore();
      } else {
        console.log('Invalid path or decision, redirecting to dashboard:', { path: location.pathname, decision });
        setModal({ visible: false, message: '', isProcessing: false });
        navigate('/dashboard');
      }
    } else {
      console.log('No authenticated user, redirecting to sign-in');
      setModal({ visible: false, message: '', isProcessing: false });
      navigate('/sign-in');
    }
  }, [user, loadingAuth, decision, transactionId, referenceNumber, amount, navigate, location.pathname]);

  const closeModal = () => {
    setModal({ visible: false, message: '', isProcessing: false });
    navigate('/dashboard');
  };

  return (
    <>
      {modal.visible && (
        <ModernDialog open={modal.visible}>
          <DialogTitle sx={{ background: colors.primary, color: '#fff', fontWeight: 700 }}>
            Payment Status
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {modal.isProcessing ? (
              <>
                <CircularProgress sx={{ color: colors.primary, mb: 2 }} />
                <Typography>Processing your payment...</Typography>
              </>
            ) : (
              <Typography sx={{ mb: 2 }}>Payment Successful!</Typography>
            )}
          </DialogContent>
          {!modal.isProcessing && (
            <DialogActions sx={{ p: 3 }}>
              <SelectButton onClick={closeModal}>
                Back to Dashboard
              </SelectButton>
            </DialogActions>
          )}
        </ModernDialog>
      )}
    </>
  );
};

export default ResponsePage;